<template>
  <div class="substep-set-up-time-limits">
    <div class="substep-header">
      <h3 data-testId="step-title">Set up your first limits</h3>
      <p>
        Set time limits for your team members. <strong>We've recommended a common set of limits for your team.</strong>
      </p>
    </div>
    <div class="substep-body">
      <div class="limits-tables">
        <div class="title">Member limits</div>

        <div class="global-limits">
          <AppTable
            class="members-table"
            :table-data="[localGlobalLimits]"
          >
            <AppTableColumn
              header="Global limits"
              header-classes="member-column"
            >
              <div class="member-info" />
            </AppTableColumn>
            <AppTableColumn
              v-slot="{ row }"
              header="Weekly limit"
            >
              <div class="limit-input">
                <div class="input-group">
                  <AppInput
                    v-model="row.weekly_limit"
                    type="number"
                    min="1"
                    max="168"
                    @blur="validateLimit($event, row, 'weekly_limit')"
                  />
                  <span class="input-group-addon">hrs</span>
                </div>
              </div>
            </AppTableColumn>
            <AppTableColumn
              v-slot="{ row }"
              header="Daily limit"
            >
              <div class="limit-input">
                <div class="input-group">
                  <AppInput
                    v-model="row.daily_limit"
                    type="number"
                    min="1"
                    max="24"
                    @blur="validateLimit($event, row, 'daily_limit')"
                  />
                  <span class="input-group-addon">hrs</span>
                </div>
              </div>
            </AppTableColumn>
          </AppTable>
        </div>
        <div class="members-limits">
          <AppTable
            :table-data="localMembers"
            class="members-table"
          >
            <AppTableColumn
              v-slot="{ row }"
              header="Individual limits"
              header-tooltip="Override the organization default for specific members"
              header-classes="member-column"
            >
              <div class="member-info">
                <AppAvatar
                  v-if="row.avatar"
                  size="speck"
                  :avatar="row.avatar"
                />
                <AppAvatarInitial
                  v-else
                  size="speck"
                  :text="row.name"
                />

                <span>{{ row.name }}</span>
              </div>
            </AppTableColumn>
            <AppTableColumn
              v-slot="{ row }"
              header="Weekly limit"
            >
              <div class="limit-input">
                <div class="input-group">
                  <AppInput
                    v-model="row.weekly_limit"
                    type="number"
                    min="1"
                    max="168"
                    @blur="validateLimit($event, row, 'weekly_limit')"
                  />
                  <span class="input-group-addon">hrs</span>
                </div>
              </div>
            </AppTableColumn>
            <AppTableColumn
              v-slot="{ row }"
              header="Daily limit"
            >
              <div class="limit-input">
                <div class="input-group">
                  <AppInput
                    v-model="row.daily_limit"
                    type="number"
                    min="1"
                    max="24"
                    @blur="validateLimit($event, row, 'daily_limit')"
                  />
                  <span class="input-group-addon">hrs</span>
                </div>
              </div>
            </AppTableColumn>
          </AppTable>
        </div>
      </div>

      <div class="limits-alert alert alert-info">
        Members can also request more time above their limit. If a user has no set limit, they will use the global limit.
      </div>
    </div>
  </div>
</template>

<script>
import GettingStartedMixin from '~/mixins/GettingStarted/GettingStartedMixin';
import { UserOrganizationsResource, OrganizationsResource } from '~/resources';
import AppInput from '~/components/AppInput.vue';
import AppTable from '~/components/AppTable.vue';
import AppTableColumn from '~/components/AppTableColumn.vue';
import AppAvatar from '~/components/AppAvatar.vue';
import AppAvatarInitial from '~/components/AppAvatarInitial.vue';

export default {
  name: 'SetUpTimeLimits',
  components: {
    AppAvatar,
    AppAvatarInitial,
    AppInput,
    AppTable,
    AppTableColumn,
  },
  mixins: [GettingStartedMixin],
  props: {
    organization: {
      type: Object,
      required: true,
    },
    members: {
      type: Array,
      default: () => [],
    },
    globalLimits: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      defaultWeeklyLimit: 40,
      defaultDailyLimit: 8,
      localMembers: this.members.map(member => ({ ...member })),
      localGlobalLimits: { ...this.globalLimits },
    };
  },
  created() {
    this.$parent.$on('userClickedContinue', this.handleFinishedSubstep);
  },
  mounted() {
    this.$emit('completeSubstep');
    this.localGlobalLimits.weekly_limit = this.localGlobalLimits.weekly_limit || this.defaultWeeklyLimit;
    this.localGlobalLimits.daily_limit = this.localGlobalLimits.daily_limit || this.defaultDailyLimit;
  },
  methods: {
    validateLimit(event, item, field) {
      const min = parseFloat(event.target.min);
      const max = parseFloat(event.target.max);
      const value = parseFloat(item[field]);

      if (isNaN(value)) {
        item[field] = '';
      } else if (value < min) {
        item[field] = min;
      } else if (value > max) {
        item[field] = max;
      }
    },
    async handleFinishedSubstep() {
      const globalOk = await this.updateGlobalLimits();

      if (!globalOk) {
        return;
      }

      const membersOk = await this.updateMembers();

      if (membersOk) {
        this.$emit('finishSubstep');
      }
    },
    async updateGlobalLimits() {
      try {
        const weeklyLimit = parseInt(this.localGlobalLimits.weekly_limit, 10) || 0;
        const expected_per_week = weeklyLimit ? Math.min(40, weeklyLimit) : 40;
        const payload = {
          ...this.localGlobalLimits,
          expected_per_week,
          dow_limit: {
            0: 1, 1: 2, 2: 4, 3: 8, 4: 16, 5: 32, 6: 64
          },
          dow_expected: {
            1: 2, 2: 4, 3: 8, 4: 16, 5: 32
          }
        };

        await OrganizationsResource.updateWorkLimitsAndExpectations(
          { id: this.organization.id },
          { work_limits_and_expectations: payload },
          { abortReload: true }
        );

        return true;
      } catch (error) {
        if (![401, 403].includes(error.status)) {
          this.$appMessage('Unhandled error. Please try again.');
        }

        return false;
      }
    },
    updateMember(member) {
      const weekly_limit = (member.weekly_limit === '' || member.weekly_limit == null) ? null : member.weekly_limit;
      const daily_limit = (member.daily_limit === '' || member.daily_limit == null) ? null : member.daily_limit;

      return UserOrganizationsResource.update(
        { id: member.id },
        {
          user_organization: {
            limits: {
              weekly_limit,
              daily_limit,
              expected_per_week: weekly_limit
            }
          },
        },
        { abortReload: true }
      );
    },
    async updateMembers() {
      try {
        const promises = this.localMembers.map((member) => {
          const memberWithLimits = { ...member };

          if (memberWithLimits.weekly_limit === '' || memberWithLimits.weekly_limit == null) {
            memberWithLimits.weekly_limit = this.localGlobalLimits.weekly_limit;
          }
          if (memberWithLimits.daily_limit === '' || memberWithLimits.daily_limit == null) {
            memberWithLimits.daily_limit = this.localGlobalLimits.daily_limit;
          }

          return this.updateMember(memberWithLimits);
        });

        if (promises.length === 0) {
          return true;
        }

        const results = await Promise.allSettled(promises);
        const failed = results.some(result => result.status === 'rejected');

        if (failed) {
          this.$appMessage('An error occurred while updating some members. Please try again.');

          return false;
        }

        return true;
      } catch (error) {
        if (![401, 403].includes(error.status)) {
          this.$appMessage('Unhandled error. Please try again.');
        }

        return false;
      }
    },
  },
};
</script>
